/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, Music, Lock } from 'lucide-react';
import { BackingTrack } from '../../types/jamTrack';
import { fetchBackingTracksByScale } from '../../api';

interface BackingTrackPanelProps {
  currentKey: string;
  currentScale: string; // Frontend scale value (e.g., 'dorian', 'aeolian')
  onTrackSelect: (track: BackingTrack) => void;
  isVisible: boolean;
  inMainArea?: boolean; // Whether this panel is in the main content area vs sidebar
  onTracksCountChange?: (count: number) => void; // Callback for tracks count changes
  isAnonymous?: boolean; // Whether user is anonymous (not signed in)
}

const BackingTrackPanel: React.FC<BackingTrackPanelProps> = ({
  currentKey,
  currentScale,
  onTrackSelect,
  isVisible,
  inMainArea = false,
  onTracksCountChange,
  isAnonymous = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed to save space
  const [tracks, setTracks] = useState<BackingTrack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tracks when key/scale changes and panel is visible
  useEffect(() => {
    const fetchTracks = async () => {
      if (!isVisible || !currentKey || !currentScale) {
        setTracks([]);
        if (onTracksCountChange) {
          onTracksCountChange(0);
        }
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const backingTracks = await fetchBackingTracksByScale(currentKey, currentScale);
        setTracks(backingTracks);
        
        // Notify parent of tracks count change
        if (onTracksCountChange) {
          onTracksCountChange(backingTracks.length);
        }
        
        // Start collapsed by default to save space
        setIsExpanded(false);
      } catch (err) {
        console.error('Error fetching backing tracks:', err);
        setError('Failed to load backing tracks');
        setTracks([]);
        
        // Notify parent of zero tracks
        if (onTracksCountChange) {
          onTracksCountChange(0);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTracks();
  }, [currentKey, currentScale, isVisible]);


  // Get genre badge color
  const getGenreColor = (genre?: string): string => {
    const colors: Record<string, string> = {
      'Rock': 'bg-red-100 text-red-800',
      'Pop': 'bg-blue-100 text-blue-800', 
      'Blues': 'bg-indigo-100 text-indigo-800',
      'Jazz': 'bg-purple-100 text-purple-800',
      'Country': 'bg-orange-100 text-orange-800',
      'Folk': 'bg-green-100 text-green-800'
    };
    
    return colors[genre || ''] || 'bg-gray-100 text-gray-800';
  };

  if (!isVisible) return null;

  return (
    <div className={`backing-track-panel ${inMainArea ? `mr-6 flex-shrink-0 ${isExpanded ? (tracks.length > 0 ? 'w-80' : 'w-64') : 'w-auto'}` : 'mb-4'}`}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className={`${isExpanded ? 'w-full' : 'w-auto'} justify-between p-3 h-auto`}
            disabled={isLoading}
          >
            <div className="flex items-center gap-2">
              <Music className="h-5 w-5 text-primary" />
              <span className="font-semibold text-lg">
                Jam Tracks
                {tracks.length > 0 && (
                  <span className="ml-1 text-primary">({tracks.length})</span>
                )}
                {tracks.length === 0 && !isLoading && !error && (
                  <span className="ml-1 text-muted-foreground">(0)</span>
                )}
              </span>
              {isLoading && (
                <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
              )}
            </div>
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-muted-foreground">
                Compatible with {currentKey} {currentScale === 'aeolian' ? 'Minor' : currentScale === 'Ionian' ? 'Major' : currentScale}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="pt-0">
              {error && (
                <div className="text-red-600 text-sm p-3 bg-red-50 rounded-md mb-3">
                  {error}
                </div>
              )}

              {tracks.length === 0 && !isLoading && !error && (
                <div className="text-center py-4 text-muted-foreground">
                  <Music className="h-6 w-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No jam tracks found</p>
                  <p className="text-xs mt-1 opacity-75">Try a different scale</p>
                </div>
              )}

              {tracks.length > 0 && (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {tracks.map((track) => (
                    <div
                      key={track.id}
                      className={`p-3 bg-muted/50 rounded-lg transition-colors ${
                        isAnonymous
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:bg-muted/80'
                      }`}
                      onClick={() => {
                        if (isAnonymous) {
                          alert('🔒 Jam tracks require a free account!\n\nSign up free to unlock C major and C minor scales with Harmonic Companion AI chat. Note: Jam tracks require Pro tier upgrade.');
                        } else {
                          onTrackSelect(track);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm mb-1 leading-tight">
                              {track.title || 'Untitled Track'}
                            </h4>
                            {isAnonymous && <Lock className="h-3 w-3 text-muted-foreground" />}
                          </div>

                          {track.bpm && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                              <span>{track.bpm} BPM</span>
                            </div>
                          )}

                          {track.genre && (
                            <Badge
                              variant="secondary"
                              className={`text-xs ${getGenreColor(track.genre)}`}
                            >
                              {track.genre}
                            </Badge>
                          )}
                        </div>

                        <Button size="sm" variant="outline" disabled={isAnonymous}>
                          {isAnonymous ? <Lock className="h-3 w-3" /> : 'Play'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default BackingTrackPanel;